import {
  DEFAULT_NOTIFICATION_DISPLAY,
  levelShowsUnreadCount,
  mentionPassesLevel,
  messagePassesLevel,
  type ChatIncomingMessage,
  type SpaceNotificationDisplay,
  type MessageMentionKind,
  type PublicIdentity,
  type PublicSpace,
  type PublicSpaceChannel,
  type PublicSpaceAnnouncement,
  type PublicSpaceChannelCategory,
  type PublicSpaceMessage,
  type PublicSpaceMessageThread,
  type SpaceChangeKind,
  type SpaceChangeNotice,
  type SpaceNotifyLevel,
} from '@adieuu/shared';
import { announcementToastSummary, parseAnnouncementPayload } from './announcementPayload';
import {
  describeSpaceNotification,
  fallbackNotificationTranslate,
  type NotificationCopy,
  type SpaceNotificationKind,
} from '../utils/notificationCopy';
import {
  emitSpaceInvitesChanged,
  emitSpaceMemberJoined,
  emitSpaceMemberLeft,
  emitSpaceMemberUpdated,
  emitSpacesChanged,
} from './spacesMembershipEvents';
import { sidebarActions } from '../utils/sidebarActions';
import { messageCiphertextIsUnreadable } from './spaceChannelReadability';
import { applyThreadSummary, emitSpaceThreadEvent } from './spaceThreadEvents';

export interface SpaceChannelUnreadState {
  unread: number;
  mention: boolean;
  /** Owning Space id — used to clear unread for a whole Space. */
  spaceId: string;
}

export interface SpaceSocketHandlerContext {
  setSpaces: (updater: (prev: PublicSpace[]) => PublicSpace[]) => void;
  /** Keeps the open Space (header, theme) in sync with `space_updated`. */
  setActiveSpace?: (updater: (prev: PublicSpace | null) => PublicSpace | null) => void;
  /** The viewer's own membership changed (roles, etc.): re-resolve permissions. */
  onViewerMemberUpdated?: (spaceId: string) => void;
  setChannels?: (updater: (prev: PublicSpaceChannel[]) => PublicSpaceChannel[]) => void;
  setCategories?: (
    updater: (prev: PublicSpaceChannelCategory[]) => PublicSpaceChannelCategory[],
  ) => void;
  setMessagesByChannel: (
    updater: (
      prev: Record<string, { messages: PublicSpaceMessage[]; olderCursor: string | null; loading: boolean }>,
    ) => Record<string, { messages: PublicSpaceMessage[]; olderCursor: string | null; loading: boolean }>,
  ) => void;
  activeSpaceId: string | null;
  activeChannelId: string | null;
  /** Whether the document/window currently has focus (for viewing-suppress). */
  hasFocus?: boolean;
  identityId: string | undefined;
  /**
   * Role IDs the viewer holds in the active Space (includes the Everyone role).
   * Used to derive @role / @everyone mentions locally for the active Space; the
   * server no longer expands group mentions into a per-recipient audience.
   */
  activeSpaceRoleIds?: string[];
  /**
   * The viewer's role IDs per joined Space (from the Space list), so @role
   * mentions resolve outside the active Space too.
   */
  viewerRoleIdsBySpace?: Record<string, string[]>;
  /**
   * Effective notification level for a channel. `categoryPath` is nearest-first.
   * When omitted every channel behaves as `all`.
   */
  resolveNotifyLevel?: (
    spaceId: string,
    channelId: string,
    categoryPath: readonly string[],
  ) => SpaceNotifyLevel;
  /** Whether new announcements in a Space should alert. Omitted = never. */
  resolveAnnouncements?: (spaceId: string) => boolean;
  /** Whether settings changes made by others in a Space should alert. Omitted = never. */
  resolveSpaceChanges?: (spaceId: string) => boolean;
  /** Opens a Space (Space-change notification click). */
  openSpace?: (spaceId: string) => void;
  /** Display name for a joined Space (decrypted when possible). */
  spaceNameFor?: (spaceId: string) => string | undefined;
  /** Opens an announcement (notification click). */
  openAnnouncement?: (spaceId: string, announcementId: string) => void;
  fetchChannelMessages: (spaceId: string, channelId: string) => void;
  refreshSpaces: () => void;
  onSocketReactionAdded?: (reaction: {
    id: string;
    messageId: string;
    channelId: string;
    fromIdentityId: string;
    emoji?: string;
    tag?: string;
    count?: number;
    createdAt: string;
  }) => void;
  onSocketReactionRemoved?: (
    messageId: string,
    reactionId: string,
    extra?: { tag?: string; count?: number; identityId?: string },
  ) => void;
  onSocketPinsUpdated?: (messageId: string, action: 'pinned' | 'unpinned') => void;
  setUnreadByChannel?: (
    updater: (prev: Record<string, SpaceChannelUnreadState>) => Record<string, SpaceChannelUnreadState>,
  ) => void;
  setUnreadBySpace?: (
    updater: (prev: Record<string, number>) => Record<string, number>,
  ) => void;
  fireNotification?: (
    title: string,
    body: string,
    options?: {
      isMention?: boolean;
      isViewingChannel?: boolean;
      channelId?: string;
      spaceId?: string;
      spaceSlug?: string;
      onClick?: () => void;
    },
  ) => void;
  /** Resolved channel names keyed by channel ID, used for notification copy. */
  channelNames?: Record<string, string>;
  /**
   * Ciphertext messages are notified only when this returns true.
   * When omitted, ciphertext is treated as unreadable (no toast, no unread bump).
   * Plaintext messages ignore this callback.
   */
  canReadEncryptedMessage?: (msg: PublicSpaceMessage) => boolean;
  /** Resolved participant profiles, used for notification author names. */
  participantProfiles?: Record<string, PublicIdentity>;
  /** Messages in the active channel, used for reaction author lookup. */
  activeChannelMessages?: PublicSpaceMessage[];
  /** Called when a Space is permanently deleted (after local list cleanup). */
  onSpaceDeleted?: (spaceId: string) => void;
  /** Optional i18n helper for membership notifications. */
  t?: (key: string, options?: Record<string, unknown>) => string;
  /** Which details message notifications reveal. Omitted = show everything. */
  notificationDisplay?: SpaceNotificationDisplay;
}

/** Alert routing the provider refreshes every render (announcements, Space changes). */
export type SpaceAlertsContext = Pick<
  SpaceSocketHandlerContext,
  | 'resolveAnnouncements'
  | 'resolveSpaceChanges'
  | 'spaceNameFor'
  | 'openAnnouncement'
  | 'openSpace'
  | 'notificationDisplay'
>;

function messageNotificationCopy(
  ctx: SpaceSocketHandlerContext,
  spaceId: string,
  channelName: string | undefined,
  kind: SpaceNotificationKind,
  senderName: string | undefined,
  emoji?: string,
): NotificationCopy {
  return describeSpaceNotification({
    kind,
    senderName,
    channelName,
    spaceName: ctx.spaceNameFor?.(spaceId),
    emoji,
    display: ctx.notificationDisplay ?? DEFAULT_NOTIFICATION_DISPLAY.spaces,
    t: ctx.t ?? fallbackNotificationTranslate,
  });
}

interface ViewerMention {
  /** Explicit @user (`mentionedIdentityIds`). */
  alias: boolean;
  replyToMe: boolean;
  /** @here (delivery is already audience-scoped) or a held @role / @everyone. */
  group: boolean;
}

/**
 * How an incoming Space message addresses the viewer, derived client-side from
 * the message's lightweight markers. Roles come from the active Space's
 * membership, falling back to the Space list's `viewerRoleIds` elsewhere.
 */
function viewerMention(
  msg: PublicSpaceMessage,
  ctx: Pick<
    SpaceSocketHandlerContext,
    'identityId' | 'activeSpaceId' | 'activeSpaceRoleIds' | 'viewerRoleIdsBySpace'
  >,
): ViewerMention {
  const me = ctx.identityId;
  if (!me) return { alias: false, group: false, replyToMe: false };
  const replyToMe =
    (!!msg.replyToMessageId &&
      typeof msg.replyToMessageAuthorId === 'string' &&
      msg.replyToMessageAuthorId === me) ||
    (!!msg.threadRootId && msg.threadRootAuthorId === me);
  const alias = msg.mentionedIdentityIds?.includes(me) ?? false;
  let group = msg.mentionHere === true;
  if (!group && msg.mentionedRoleIds?.length) {
    const roleIds =
      msg.spaceId === ctx.activeSpaceId && ctx.activeSpaceRoleIds?.length
        ? ctx.activeSpaceRoleIds
        : ctx.viewerRoleIdsBySpace?.[msg.spaceId];
    if (roleIds?.length) {
      const held = new Set(roleIds);
      group = msg.mentionedRoleIds.some((roleId) => held.has(roleId));
    }
  }
  return { alias, group, replyToMe };
}

/** Whether a `change` notice on a Space broadcast should alert this viewer. */
function shouldAlertSpaceChange(
  ctx: SpaceSocketHandlerContext,
  spaceId: string,
  change: SpaceChangeNotice | undefined,
): change is SpaceChangeNotice {
  if (!change || change.changes.length === 0 || !ctx.fireNotification) return false;
  if (!ctx.identityId || change.actorId === ctx.identityId) return false;
  return ctx.resolveSpaceChanges?.(spaceId) === true;
}

function spaceChangeTitle(ctx: SpaceSocketHandlerContext): string {
  return ctx.t?.('spaces.notifications.spaceChangedTitle', { defaultValue: 'Space updated' }) ?? 'Space updated';
}

const CHANGE_KIND_FALLBACKS: Record<SpaceChangeKind, string> = {
  name: 'name',
  description: 'description',
  logo: 'icon',
  banner: 'banner',
  theme: 'theme',
  visibility: 'visibility',
  access: 'membership access',
  encryption: 'encryption settings',
};

function describeChangeKinds(ctx: SpaceSocketHandlerContext, changes: readonly SpaceChangeKind[]): string {
  const labels = changes.map((kind) => {
    const fallback = CHANGE_KIND_FALLBACKS[kind] ?? kind;
    return ctx.t?.(`spaces.notifications.changeKinds.${kind}`, { defaultValue: fallback }) ?? fallback;
  });
  return new Intl.ListFormat(undefined, { style: 'long', type: 'conjunction' }).format(labels);
}

function spaceNameOrFallback(ctx: SpaceSocketHandlerContext, spaceId: string): string {
  return (
    ctx.spaceNameFor?.(spaceId) ??
    ctx.t?.('spaces.notifications.someSpace', { defaultValue: 'A Space' }) ??
    'A Space'
  );
}

/** Readable name of a channel/category for alert copy; encrypted names stay hidden. */
function plaintextName(item: { name: string; encryptedName?: string }): string | undefined {
  return !item.encryptedName && item.name ? item.name : undefined;
}

/** Toast summary for a plaintext announcement; encrypted ones are not opened here. */
function readableAnnouncementText(announcement: PublicSpaceAnnouncement): string | null {
  if (typeof announcement.content !== 'string' || announcement.ciphertext) return null;
  const summary = announcementToastSummary(parseAnnouncementPayload(announcement.content));
  return summary.text || null;
}

/** Replace a buffered thread root's summary with the server's copy. */
function patchThreadRoot(
  ctx: SpaceSocketHandlerContext,
  channelId: string,
  rootId: string,
  summary: PublicSpaceMessageThread | null,
): void {
  ctx.setMessagesByChannel((prev) => {
    const state = prev[channelId];
    if (!state) return prev;
    let changed = false;
    const messages = state.messages.map((m) => {
      if (m.id !== rootId) return m;
      const next = applyThreadSummary(m, summary);
      if (next !== m) changed = true;
      return next;
    });
    return changed ? { ...prev, [channelId]: { ...state, messages } } : prev;
  });
}

/**
 * Testable, React-free handler for Space WebSocket events.
 *
 * Mirrors {@link handleConversationSocketMessage} from
 * `conversationSocketHandlers.ts`. Invite events refetch the authorized
 * inbox; a received invite also fires the toast/sound path.
 */
export function handleSpaceSocketMessage(
  message: ChatIncomingMessage,
  ctx: SpaceSocketHandlerContext,
): void {
  switch (message.type) {
    case 'space_created': {
      const { space } = message.data;
      ctx.setSpaces((prev) => {
        if (prev.some((s) => s.id === space.id)) return prev;
        return [space, ...prev];
      });
      emitSpacesChanged();
      break;
    }

    case 'space_updated': {
      const { space } = message.data;
      ctx.setSpaces((prev) =>
        prev.map((s) =>
          s.id === space.id
            ? { ...space, ...(s.viewerRoleIds ? { viewerRoleIds: s.viewerRoleIds } : {}) }
            : s,
        ),
      );
      // Viewer-scoped fields (membershipStatus, banExpiresAt) are not in the
      // broadcast payload, so keep the ones already on the open Space.
      ctx.setActiveSpace?.((prev) =>
        prev && prev.id === space.id
          ? {
              ...space,
              ...(prev.membershipStatus ? { membershipStatus: prev.membershipStatus } : {}),
              ...(prev.banExpiresAt !== undefined ? { banExpiresAt: prev.banExpiresAt } : {}),
            }
          : prev,
      );
      const { change } = message.data;
      if (shouldAlertSpaceChange(ctx, space.id, change)) {
        const t = ctx.t;
        const name = ctx.spaceNameFor?.(space.id) ?? (space.name || undefined);
        const changes = describeChangeKinds(ctx, change.changes);
        const body = name
          ? (t?.('spaces.notifications.spaceChangedBody', {
              name,
              changes,
              defaultValue: `${name}: ${changes} changed`,
            }) ?? `${name}: ${changes} changed`)
          : (t?.('spaces.notifications.spaceChangedBodyGeneric', {
              changes,
              defaultValue: `A Space you are in changed its ${changes}`,
            }) ?? `A Space you are in changed its ${changes}`);
        ctx.fireNotification?.(spaceChangeTitle(ctx), body, {
          spaceId: space.id,
          isViewingChannel: space.id === ctx.activeSpaceId && !!ctx.hasFocus,
          onClick: () => ctx.openSpace?.(space.id),
        });
      }
      break;
    }

    case 'space_deleted': {
      const { spaceId } = message.data;
      ctx.setSpaces((prev) => prev.filter((s) => s.id !== spaceId));
      ctx.setUnreadBySpace?.((prev) => {
        if (!prev[spaceId]) return prev;
        const { [spaceId]: _, ...rest } = prev;
        return rest;
      });
      emitSpacesChanged();
      ctx.onSpaceDeleted?.(spaceId);
      break;
    }

    case 'space_channel_created':
    case 'space_channel_updated': {
      const { channel } = message.data;
      const change = message.type === 'space_channel_updated' ? message.data.change : undefined;
      if (shouldAlertSpaceChange(ctx, channel.spaceId, change)) {
        const t = ctx.t;
        const name = spaceNameOrFallback(ctx, channel.spaceId);
        const channelName = ctx.channelNames?.[channel.id] ?? plaintextName(channel);
        const body = channelName
          ? (t?.('spaces.notifications.channelEncryptionChangedBody', {
              name,
              channel: channelName,
              defaultValue: `${name}: encryption settings changed for #${channelName}`,
            }) ?? `${name}: encryption settings changed for #${channelName}`)
          : (t?.('spaces.notifications.channelEncryptionChangedBodyGeneric', {
              name,
              defaultValue: `${name}: encryption settings changed for a channel`,
            }) ?? `${name}: encryption settings changed for a channel`);
        ctx.fireNotification?.(spaceChangeTitle(ctx), body, {
          spaceId: channel.spaceId,
          channelId: channel.id,
          isViewingChannel:
            channel.spaceId === ctx.activeSpaceId && channel.id === ctx.activeChannelId && !!ctx.hasFocus,
        });
      }
      if (channel.spaceId !== ctx.activeSpaceId) break;
      ctx.setChannels?.((prev) => {
        if (prev.some((c) => c.id === channel.id)) {
          return prev.map((c) => (c.id === channel.id ? channel : c));
        }
        if (message.type === 'space_channel_updated') return prev;
        return [...prev, channel].sort(
          (a, b) => a.position - b.position || a.id.localeCompare(b.id),
        );
      });
      break;
    }

    case 'space_category_created':
    case 'space_category_updated': {
      const { category } = message.data;
      const change = message.type === 'space_category_updated' ? message.data.change : undefined;
      if (shouldAlertSpaceChange(ctx, category.spaceId, change)) {
        const t = ctx.t;
        const name = spaceNameOrFallback(ctx, category.spaceId);
        const categoryName = plaintextName(category);
        const body = categoryName
          ? (t?.('spaces.notifications.categoryEncryptionChangedBody', {
              name,
              category: categoryName,
              defaultValue: `${name}: encryption settings changed for ${categoryName}`,
            }) ?? `${name}: encryption settings changed for ${categoryName}`)
          : (t?.('spaces.notifications.categoryEncryptionChangedBodyGeneric', {
              name,
              defaultValue: `${name}: encryption settings changed for a category`,
            }) ?? `${name}: encryption settings changed for a category`);
        ctx.fireNotification?.(spaceChangeTitle(ctx), body, {
          spaceId: category.spaceId,
          isViewingChannel: category.spaceId === ctx.activeSpaceId && !!ctx.hasFocus,
          onClick: () => ctx.openSpace?.(category.spaceId),
        });
      }
      if (category.spaceId !== ctx.activeSpaceId) break;
      ctx.setCategories?.((prev) => {
        if (prev.some((c) => c.id === category.id)) {
          return prev.map((c) => (c.id === category.id ? category : c));
        }
        if (message.type === 'space_category_updated') return prev;
        return [...prev, category].sort(
          (a, b) => a.position - b.position || a.id.localeCompare(b.id),
        );
      });
      break;
    }

    case 'space_category_deleted': {
      const { spaceId, categoryId } = message.data;
      if (spaceId !== ctx.activeSpaceId) break;
      ctx.setCategories?.((prev) => prev.filter((c) => c.id !== categoryId));
      ctx.setChannels?.((prev) =>
        prev.map((ch) =>
          ch.categoryId === categoryId ? { ...ch, categoryId: null } : ch,
        ),
      );
      break;
    }

    case 'space_channel_layout_updated': {
      const { spaceId, categories, channels } = message.data;
      if (spaceId !== ctx.activeSpaceId) break;
      ctx.setCategories?.(() => categories);
      ctx.setChannels?.(() => channels);
      try {
        const key = `adieuu:lastChannel:${spaceId}`;
        const last = localStorage.getItem(key);
        if (last && !channels.some((ch) => ch.id === last)) {
          localStorage.removeItem(key);
        }
      } catch {
        /* quota / SSR */
      }
      break;
    }

    case 'space_message': {
      const { message: msg, threadSummary } = message.data;
      const isActiveChannel =
        msg.channelId === ctx.activeChannelId &&
        msg.spaceId === ctx.activeSpaceId;

      if (msg.threadRootId) {
        if (threadSummary !== undefined) {
          patchThreadRoot(ctx, msg.channelId, msg.threadRootId, threadSummary);
        }
        emitSpaceThreadEvent({
          kind: 'message',
          message: msg,
          ...(threadSummary !== undefined ? { threadSummary } : {}),
        });
      }

      if (isActiveChannel && !msg.threadOnly) {
        ctx.setMessagesByChannel((prev) => {
          const state = prev[msg.channelId];
          if (!state) return prev;
          const existing = state.messages.find((m) => m.id === msg.id);
          if (existing) {
            return {
              ...prev,
              [msg.channelId]: {
                ...state,
                messages: state.messages.map((m) =>
                  m.id === msg.id ? msg : m,
                ),
              },
            };
          }
          return {
            ...prev,
            [msg.channelId]: {
              ...state,
              messages: [msg, ...state.messages],
            },
          };
        });
      }

      const isFromSelf = msg.fromIdentityId === ctx.identityId;
      const unreadable = messageCiphertextIsUnreadable(msg, ctx.canReadEncryptedMessage);
      if (isFromSelf || unreadable) break;

      const level: SpaceNotifyLevel =
        ctx.resolveNotifyLevel?.(msg.spaceId, msg.channelId, message.data.categoryPath ?? []) ?? 'all';
      const mention = viewerMention(msg, ctx);
      // Thread-only replies stay out of unread counts and channel-level alerts;
      // only a direct @mention or a reply to the viewer's root can alert.
      if (msg.threadOnly) {
        if (!mention.alias && !mention.replyToMe) break;
        if (ctx.fireNotification && messagePassesLevel(level, { direct: true, group: false })) {
          const senderProfile = ctx.participantProfiles?.[msg.fromIdentityId];
          const { title, body } = messageNotificationCopy(
            ctx,
            msg.spaceId,
            ctx.channelNames?.[msg.channelId] ?? message.data.channelName,
            mention.alias ? 'mention' : 'reply',
            senderProfile?.displayName ?? senderProfile?.username,
          );
          ctx.fireNotification(title, body, {
            channelId: msg.channelId,
            spaceId: msg.spaceId,
            isMention: mention.alias,
            isViewingChannel: isActiveChannel && !!ctx.hasFocus,
          });
        }
        break;
      }
      // Badges match the server summary, which cannot see reply targets.
      const isMention = mentionPassesLevel(level, { direct: mention.alias, group: mention.group });
      const alertKind: MessageMentionKind = {
        direct: mention.alias || mention.replyToMe,
        group: mention.group,
      };
      const countsUnread = levelShowsUnreadCount(level);

      if (!isActiveChannel && (countsUnread || isMention)) {
        ctx.setUnreadByChannel?.((prev) => {
          const cur = prev[msg.channelId] ?? {
            unread: 0,
            mention: false,
            spaceId: msg.spaceId,
          };
          return {
            ...prev,
            [msg.channelId]: {
              unread: cur.unread + (countsUnread ? 1 : 0),
              mention: cur.mention || isMention,
              spaceId: cur.spaceId || msg.spaceId,
            },
          };
        });
        if (countsUnread) {
          ctx.setUnreadBySpace?.((prev) => ({
            ...prev,
            [msg.spaceId]: (prev[msg.spaceId] ?? 0) + 1,
          }));
        }
      }

      if (ctx.fireNotification && messagePassesLevel(level, alertKind)) {
        const channelName = ctx.channelNames?.[msg.channelId] ?? message.data.channelName;
        const senderProfile = ctx.participantProfiles?.[msg.fromIdentityId];
        const senderName = senderProfile?.displayName ?? senderProfile?.username;
        // Suppress sound only when actively viewing this channel in a focused window.
        const isViewingChannel = isActiveChannel && !!ctx.hasFocus;
        const kind: SpaceNotificationKind = mention.replyToMe
          ? 'reply'
          : !isMention
            ? 'message'
            : mention.alias
              ? 'mention'
              : msg.mentionHere
                ? 'mentionHere'
                : 'mentionRole';
        const { title, body } = messageNotificationCopy(
          ctx,
          msg.spaceId,
          channelName,
          kind,
          senderName,
        );
        ctx.fireNotification(title, body, {
          channelId: msg.channelId,
          spaceId: msg.spaceId,
          isMention: kind === 'message' ? undefined : isMention,
          isViewingChannel,
        });
      }
      break;
    }

    case 'space_message_edited': {
      const {
        channelId,
        messageId,
        content,
        ciphertext,
        nonce,
        cipherId,
        signature,
        e2eMediaIds,
        attachments,
        attachmentMediaIds,
        lastEditedAt,
        revisionCount,
      } = message.data;
      ctx.setMessagesByChannel((prev) => {
        const state = prev[channelId];
        if (!state) return prev;
        const idx = state.messages.findIndex((m) => m.id === messageId);
        if (idx === -1) return prev;
        const updated = {
          ...state.messages[idx]!,
          lastEditedAt,
          revisionCount,
        };
        if (ciphertext !== undefined) {
          updated.ciphertext = ciphertext;
          updated.nonce = nonce;
          updated.cipherId = cipherId;
          updated.content = undefined;
          if (signature !== undefined) updated.signature = signature;
        } else if (content !== undefined) {
          updated.content = content;
        }
        if (e2eMediaIds !== undefined) updated.e2eMediaIds = e2eMediaIds;
        if (attachments !== undefined) updated.attachments = attachments;
        if (attachmentMediaIds !== undefined) updated.attachmentMediaIds = attachmentMediaIds;
        const messages = [...state.messages];
        messages[idx] = updated;
        return { ...prev, [channelId]: { ...state, messages } };
      });
      const patch: Partial<PublicSpaceMessage> = { lastEditedAt, revisionCount };
      if (ciphertext !== undefined) {
        Object.assign(patch, { ciphertext, nonce, cipherId, content: undefined });
        if (signature !== undefined) patch.signature = signature;
      } else if (content !== undefined) {
        patch.content = content;
      }
      if (e2eMediaIds !== undefined) patch.e2eMediaIds = e2eMediaIds;
      if (attachments !== undefined) patch.attachments = attachments;
      if (attachmentMediaIds !== undefined) patch.attachmentMediaIds = attachmentMediaIds;
      emitSpaceThreadEvent({ kind: 'edited', channelId, messageId, patch });
      break;
    }

    case 'space_message_deleted': {
      const { channelId, messageId, threadRootId, threadSummary } = message.data;
      ctx.setMessagesByChannel((prev) => {
        const state = prev[channelId];
        if (!state) return prev;
        return {
          ...prev,
          [channelId]: {
            ...state,
            messages: state.messages.map((m) =>
              m.id === messageId
                ? {
                    ...m,
                    deleted: true,
                    content: undefined,
                    ciphertext: undefined,
                    nonce: undefined,
                    cipherId: undefined,
                    signature: undefined,
                  }
                : m,
            ),
          },
        };
      });
      if (threadRootId && threadSummary !== undefined) {
        patchThreadRoot(ctx, channelId, threadRootId, threadSummary);
      }
      emitSpaceThreadEvent({
        kind: 'deleted',
        channelId,
        messageId,
        ...(threadRootId ? { threadRootId } : {}),
        ...(threadSummary !== undefined ? { threadSummary } : {}),
      });
      break;
    }

    case 'space_reaction_added': {
      const { reaction } = message.data;
      const socketReaction = {
        id: reaction.id,
        messageId: reaction.messageId,
        channelId: reaction.channelId,
        fromIdentityId: reaction.identityId,
        emoji: reaction.emoji,
        tag: reaction.tag ?? message.data.tag,
        count: message.data.count,
        createdAt: reaction.createdAt,
      };
      ctx.onSocketReactionAdded?.(socketReaction);
      emitSpaceThreadEvent({ kind: 'reaction_added', spaceId: reaction.spaceId, reaction: socketReaction });

      if (
        ctx.identityId &&
        reaction.identityId !== ctx.identityId &&
        ctx.fireNotification
      ) {
        const targetMsg = ctx.activeChannelMessages?.find((m) => m.id === reaction.messageId);
        // A reaction to your message alerts like a reply: every level except "Nothing".
        // Only open-channel reactions alert, so the resolver fills the empty path from the open Space's layout.
        const level = ctx.resolveNotifyLevel?.(reaction.spaceId, reaction.channelId, []) ?? 'all';
        if (
          targetMsg &&
          targetMsg.fromIdentityId === ctx.identityId &&
          messagePassesLevel(level, { direct: true, group: false })
        ) {
          const reactorProfile = ctx.participantProfiles?.[reaction.identityId];
          const reactorName = reactorProfile?.displayName ?? reactorProfile?.username;
          const { title, body } = messageNotificationCopy(
            ctx,
            reaction.spaceId,
            ctx.channelNames?.[reaction.channelId],
            'reaction',
            reactorName,
            reaction.emoji,
          );
          const isViewingChannel =
            reaction.channelId === ctx.activeChannelId &&
            reaction.spaceId === ctx.activeSpaceId &&
            !!ctx.hasFocus;
          ctx.fireNotification(title, body, {
            channelId: reaction.channelId,
            spaceId: reaction.spaceId,
            isViewingChannel,
          });
        }
      }
      break;
    }

    case 'space_reaction_removed': {
      const { messageId, reactionId, tag, count, identityId } = message.data;
      const extra =
        tag != null || count != null || identityId
          ? { tag, count, identityId }
          : undefined;
      ctx.onSocketReactionRemoved?.(messageId, reactionId, extra);
      emitSpaceThreadEvent({ kind: 'reaction_removed', messageId, reactionId, ...(extra ? { extra } : {}) });
      break;
    }

    case 'space_pins_updated': {
      const { messageId, action } = message.data;
      ctx.onSocketPinsUpdated?.(messageId, action);
      break;
    }

    case 'space_member_joined': {
      const { spaceId, member } = message.data;
      // Our own join also arrives on the identity channel (and again on the
      // Space channel once the server grants us fan-out), and the count we
      // already loaded for the Space includes us.
      if (member.identityId === ctx.identityId) break;
      ctx.setSpaces((prev) =>
        prev.map((s) =>
          s.id === spaceId ? { ...s, memberCount: s.memberCount + 1 } : s,
        ),
      );
      emitSpaceMemberJoined(spaceId, member);
      break;
    }

    case 'space_member_left': {
      const { spaceId, identityId, reason } = message.data;
      if (identityId === ctx.identityId) {
        let leavingName: string | undefined;
        ctx.setSpaces((prev) => {
          leavingName = prev.find((s) => s.id === spaceId)?.name;
          return prev.filter((s) => s.id !== spaceId);
        });
        const t = ctx.t;
        if (reason === 'banned') {
          ctx.fireNotification?.(
            t?.('spaces.notifications.bannedTitle', { defaultValue: 'Banned from Space' })
              ?? 'Banned from Space',
            leavingName
              ? (t?.('spaces.notifications.bannedBody', {
                  name: leavingName,
                  defaultValue: `You were banned from ${leavingName}`,
                }) ?? `You were banned from ${leavingName}`)
              : (t?.('spaces.notifications.bannedBodyGeneric', {
                  defaultValue: 'You were banned from a Space',
                }) ?? 'You were banned from a Space'),
            { spaceId, onClick: () => undefined },
          );
        } else if (reason === 'kicked') {
          ctx.fireNotification?.(
            t?.('spaces.notifications.kickedTitle', { defaultValue: 'Removed from Space' })
              ?? 'Removed from Space',
            leavingName
              ? (t?.('spaces.notifications.kickedBody', {
                  name: leavingName,
                  defaultValue: `You were removed from ${leavingName}`,
                }) ?? `You were removed from ${leavingName}`)
              : (t?.('spaces.notifications.kickedBodyGeneric', {
                  defaultValue: 'You were removed from a Space',
                }) ?? 'You were removed from a Space'),
            { spaceId, onClick: () => undefined },
          );
        }
        emitSpacesChanged();
      } else {
        ctx.setSpaces((prev) =>
          prev.map((s) =>
            s.id === spaceId
              ? { ...s, memberCount: Math.max(0, s.memberCount - 1) }
              : s,
          ),
        );
        emitSpaceMemberLeft(spaceId, identityId);
      }
      break;
    }

    case 'space_member_updated': {
      const { spaceId, member } = message.data;
      emitSpaceMemberUpdated(spaceId, member);
      if (ctx.identityId && member.identityId === ctx.identityId) {
        ctx.setSpaces((prev) =>
          prev.map((s) => (s.id === spaceId ? { ...s, viewerRoleIds: member.roleIds } : s)),
        );
        ctx.onViewerMemberUpdated?.(spaceId);
      }
      break;
    }

    case 'space_announcement': {
      const { spaceId, announcement, toastScope } = message.data;
      if (toastScope === 'none' || !ctx.fireNotification) break;
      if (announcement.authorId === ctx.identityId || announcement.signerId === ctx.identityId) break;
      if (ctx.resolveAnnouncements?.(spaceId) !== true) break;
      // The open, focused Space already shows its own in-channel toast.
      if (spaceId === ctx.activeSpaceId && ctx.hasFocus) break;
      const t = ctx.t;
      const name = ctx.spaceNameFor?.(spaceId);
      const text = readableAnnouncementText(announcement);
      const body = text
        ? name
          ? (t?.('spaces.notifications.announcementBody', { name, text, defaultValue: `${name}: ${text}` }) ??
            `${name}: ${text}`)
          : text
        : name
          ? (t?.('spaces.notifications.announcementBodyGeneric', {
              name,
              defaultValue: `New announcement in ${name}`,
            }) ?? `New announcement in ${name}`)
          : (t?.('spaces.notifications.announcementBodyGenericNoName', {
              defaultValue: 'New announcement in a Space',
            }) ?? 'New announcement in a Space');
      ctx.fireNotification(
        t?.('spaces.notifications.announcementTitle', { defaultValue: 'Announcement' }) ?? 'Announcement',
        body,
        { spaceId, onClick: () => ctx.openAnnouncement?.(spaceId, announcement.id) },
      );
      break;
    }

    // Invite events arrive on the identity channel and carry a payload the
    // recipient may not be entitled to keep (a revoked invite names a Space
    // they never joined). Refetch the authorized inbox rather than trusting
    // the payload to mutate membership. Toast/sound still fire on receive so
    // Space invites match group invites.
    case 'space_invite_received': {
      emitSpaceInvitesChanged();
      const invite = message.data.invite;
      const t = ctx.t;
      const spaceName = invite.spaceName;
      ctx.fireNotification?.(
        t?.('spaces.notifications.inviteTitle', { defaultValue: 'Space invitation' })
          ?? 'Space invitation',
        spaceName
          ? (t?.('spaces.notifications.inviteBody', {
              name: spaceName,
              defaultValue: `You've been invited to ${spaceName}`,
            }) ?? `You've been invited to ${spaceName}`)
          : (t?.('spaces.notifications.inviteBodyGeneric', {
              defaultValue: "You've been invited to a Space",
            }) ?? "You've been invited to a Space"),
        { onClick: () => sidebarActions.openInvites() },
      );
      break;
    }
    case 'space_invite_accepted':
    case 'space_invite_revoked':
    case 'space_invites_changed':
      emitSpaceInvitesChanged();
      break;

    // Temporary access ran out: the membership is already gone server-side, so
    // drop it locally rather than waiting for a reload to reveal the kick.
    case 'space_temporary_access_expired': {
      const { spaceId } = message.data;
      let expiredName: string | undefined;
      ctx.setSpaces((prev) => {
        expiredName = prev.find((s) => s.id === spaceId)?.name;
        return prev.filter((s) => s.id !== spaceId);
      });
      const t = ctx.t;
      ctx.fireNotification?.(
        t?.('spaces.notifications.temporaryExpiredTitle', {
          defaultValue: 'Temporary access expired',
        }) ?? 'Temporary access expired',
        expiredName
          ? (t?.('spaces.notifications.temporaryExpiredBody', {
              name: expiredName,
              defaultValue: `Your temporary access to ${expiredName} has expired`,
            }) ?? `Your temporary access to ${expiredName} has expired`)
          : (t?.('spaces.notifications.temporaryExpiredBodyGeneric', {
              defaultValue: 'Your temporary access to this Space has expired',
            }) ?? 'Your temporary access to this Space has expired'),
        { spaceId, onClick: () => undefined },
      );
      emitSpacesChanged();
      break;
    }
  }
}
